import {
  BaseTrackingAdapter,
  DeeplinkAutoOpenToggled,
  DeeplinkButtonClicked,
  DeeplinkGoToDownload,
  DeeplinkModalClosed,
  DeeplinkModalOpened,
  DeeplinkOpenInitiated,
  DeeplinkRetryButtonClicked,
  ElementClicked,
  GoToCollaborationPromo,
  GoToMembers,
  GoToReviewLinks,
  PageVisited,
  ProjectPageDownloadFileClicked,
  ProjectPageDownloadFileShown,
  ProjectPageLoaded,
  TelemetryClient,
  UrlChanged,
  WebDashboardTeamSwitcherClicked,
  WebDashboardTeamSwitcherItemClicked,
  WebDashboardViewLinkClicked,
  WebDashboardViewLinkContextMenuActed,
  WebDashboardViewLinkContextMenuOpened,
  WebDashboardViewLinkCopied,
  WebDashboardViewLinkUnpublishConfirmed,
  WebViewerActiveToolChanged,
  WebViewerAnnotationPlaced,
  WebViewerCommentAdded,
  WebViewerCommentContextMenuVisibilityChanged,
  WebViewerCommentDeleteConfirmed,
  WebViewerCommentDeleteIntended,
  WebViewerCommentingDisplayed,
  WebViewerCommentingSidebarVisibilityChanged,
  WebViewerCommentSelected,
  WebViewerDrawingDisplayed,
  WebViewerDrawingDisplayFailed,
  WebViewerDrawingDownloaded,
  WebViewerDrawingDownloadFailed,
  WebViewerModelDisplayed,
  WebViewerModelDisplayFailed,
  WebViewerModelDownloaded,
  WebViewerModelDownloadFailed,
  WebViewerShareModalEmailAdded,
  WebViewerShareModalEmailRemoved,
  WebViewerShareModalGeneralPermissionChanged,
  WebViewerShareModalGeneralRoleChanged,
  WebViewerShareModalInvalidEmailAdded,
  WebViewerShareModalInvitePressed,
  WebViewerShareModalInviteRoleChanged,
  WebViewerShareModalMemberRemoveConfirmed,
  WebViewerShareModalMemberRemoveIntended,
  WebViewerShareModalMemberRoleChanged,
  WebViewerShareModalVisibilityChanged,
  WebViewerViewLinkAccessDenied,
} from "@shapr3d/frontend-lib";

import { telemetryClientEnvironment } from "../config";
import { isInsideIframe } from "../utils/helpers";
import { version } from "../version";

export enum ElementClickAction {
  OPEN_ACCOUNT_MENU = "open_account_menu",
  CLOSE_ACCOUNT_MENU = "close_account_menu",
  GO_TO_VIDEO_TUTORIALS = "go_to_video_tutorials",
  GO_TO_ACCOUNT_SETTINGS = "go_to_account_settings",
  GO_TO_COMMUNITY = "go_to_community",
  GO_TO_DOWNLOAD = "go_to_download",
  GO_TO_HELP_CENTER = "go_to_help_center",
  DOWNLOAD_PDF = "download-pdf",
  NAVBAR_OPEN = "navbar_open",
  NAVBAR_CLOSE = "navbar_close",
  COPY_LINK = "copy_link",
  LOGIN = "login",
}

export enum ElementClickLocation {
  TOP_RIGHT_MENU = "top_right_menu",
  SHARE_MODAL = "share_modal",
  HEADER = "header",
}

export default class TrackingAdapter extends BaseTrackingAdapter {
  trackURLChanged = this.createTelemetryTrackingFunction(UrlChanged);
  trackPageView() {
    this.telemetryClient.trackTelemetryEvent(
      new PageVisited({ referrer: document.referrer, isInsideIframe: isInsideIframe() })
    );
  }

  trackButtonClick({
    elementId,
    action,
    location,
    details,
  }: {
    /**
     * @deprecated
     */
    elementId?: string;
    action?: ElementClickAction;
    location?: string;
    details?: string;
  }) {
    this.telemetryClient.trackTelemetryEvent(
      new ElementClicked({ elementId, action, location, details })
    );
  }

  trackModelDisplayed = this.createTelemetryTrackingFunction(WebViewerModelDisplayed);
  trackModelDisplayFailed = this.createTelemetryTrackingFunction(WebViewerModelDisplayFailed);
  trackModelDownloaded = this.createTelemetryTrackingFunction(WebViewerModelDownloaded);
  trackModelDownloadFailed = this.createTelemetryTrackingFunction(WebViewerModelDownloadFailed);
  trackDrawingDisplayed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayed);
  trackDrawingDisplayFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDisplayFailed);
  trackDrawingDownloaded = this.createTelemetryTrackingFunction(WebViewerDrawingDownloaded);
  trackDrawingDownloadFailed = this.createTelemetryTrackingFunction(WebViewerDrawingDownloadFailed);
  trackAnnotationPlaced = this.createTelemetryTrackingFunction(WebViewerAnnotationPlaced);
  trackCommentAdded = this.createTelemetryTrackingFunction(WebViewerCommentAdded);
  trackCommentSelected = this.createTelemetryTrackingFunction(WebViewerCommentSelected);
  trackViewLinkAccessDenied = this.createTelemetryTrackingFunction(WebViewerViewLinkAccessDenied);
  trackActiveToolChanged = this.createTelemetryTrackingFunction(WebViewerActiveToolChanged);
  trackCommentingDisplayed = this.createTelemetryTrackingFunction(WebViewerCommentingDisplayed);
  trackCommentingSidebarVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentingSidebarVisibilityChanged
  );
  trackCommentContextMenuVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerCommentContextMenuVisibilityChanged
  );
  trackCommentDeleteIntended = this.createTelemetryTrackingFunction(WebViewerCommentDeleteIntended);
  trackCommentDeleteConfirmed = this.createTelemetryTrackingFunction(
    WebViewerCommentDeleteConfirmed
  );
  trackShareModalGeneralPermissionChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralPermissionChanged
  );
  trackShareModalGeneralRoleChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalGeneralRoleChanged
  );
  trackShareModalVisibilityChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalVisibilityChanged
  );
  trackShareModalMemberRoleChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalMemberRoleChanged
  );
  trackShareModalMemberRemoveIntended = this.createTelemetryTrackingFunction(
    WebViewerShareModalMemberRemoveIntended
  );
  trackShareModalMemberRemoveConfirmed = this.createTelemetryTrackingFunction(
    WebViewerShareModalMemberRemoveConfirmed
  );
  trackShareModalInvitePressed = this.createTelemetryTrackingFunction(
    WebViewerShareModalInvitePressed
  );
  trackShareModalInviteRoleChanged = this.createTelemetryTrackingFunction(
    WebViewerShareModalInviteRoleChanged
  );
  trackShareModalEmailRemoved = this.createTelemetryTrackingFunction(
    WebViewerShareModalEmailRemoved
  );
  trackShareModalEmailAdded = this.createTelemetryTrackingFunction(WebViewerShareModalEmailAdded);
  trackShareModalInvalidEmailAdded = this.createTelemetryTrackingFunction(
    WebViewerShareModalInvalidEmailAdded
  );
  trackDashboardTeamSwitcherClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherClicked
  );
  trackDashboardTeamSwitcherItemClicked = this.createTelemetryTrackingFunction(
    WebDashboardTeamSwitcherItemClicked
  );
  trackDashboardViewLinkClicked = this.createTelemetryTrackingFunction(WebDashboardViewLinkClicked);
  trackDashboardViewMenuActed = this.createTelemetryTrackingFunction(
    WebDashboardViewLinkContextMenuActed
  );
  trackDashboardViewLinkContextMenuOpened = this.createTelemetryTrackingFunction(
    WebDashboardViewLinkContextMenuOpened
  );
  trackDashboardViewLinkCopied = this.createTelemetryTrackingFunction(WebDashboardViewLinkCopied);
  trackGoToCollaborationPromo = this.createTelemetryTrackingFunction(GoToCollaborationPromo);
  trackGoToMembers = this.createTelemetryTrackingFunction(GoToMembers);
  trackGoToReviewLinks = this.createTelemetryTrackingFunction(GoToReviewLinks);
  trackUnpublishConfirmed = this.createTelemetryTrackingFunction(
    WebDashboardViewLinkUnpublishConfirmed
  );
  trackDownloadFileClicked = this.createTelemetryTrackingFunction(ProjectPageDownloadFileClicked);
  trackProjectPageDownloadFileShown = this.createTelemetryTrackingFunction(
    ProjectPageDownloadFileShown
  );
  trackProjectPageLoaded = this.createTelemetryTrackingFunction(ProjectPageLoaded);

  // Deep link events
  trackDeeplinkAutoOpenToggled = this.createTelemetryTrackingFunction(DeeplinkAutoOpenToggled);
  trackDeeplinkButtonClicked = this.createTelemetryTrackingFunction(DeeplinkButtonClicked);
  trackDeeplinkGoToDownload = this.createTelemetryTrackingFunction(DeeplinkGoToDownload);
  trackDeeplinkModalClosed = this.createTelemetryTrackingFunction(DeeplinkModalClosed);
  trackDeeplinkModalOpened = this.createTelemetryTrackingFunction(DeeplinkModalOpened);
  trackDeeplinkOpenInitiated = this.createTelemetryTrackingFunction(DeeplinkOpenInitiated);
  trackDeeplinkRetryButtonClicked = this.createTelemetryTrackingFunction(
    DeeplinkRetryButtonClicked
  );
}

export const trackingAdapter = new TrackingAdapter(
  new TelemetryClient({
    appName: "webviewer",
    siteVersion: version,
    environment: telemetryClientEnvironment,
  })
);
